'use client';

/**
 * Removable list of scoped evidence items.
 *
 * Candidate for promotion to `@tranquility-ai/theme-components`. This is a
 * generic "scoped items" pattern (grouped removable tags, collapse, clear)
 * and is not specific to timelines. Do not add it to the library without a
 * Figma draft on Serenity `99 Drafts` (`figma-mcp.mdc`).
 */

import React, { useState } from 'react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Tag } from '@tranquility-ai/theme-components/tag';
import { TpTooltip } from '@tranquility-ai/theme-components/tooltip';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import type { ScopesObject } from '@/shared/types';
import {
  clearScopedItems,
  listScopedItems,
  removeScopedItem,
  type ScopedItem,
  type ScopedItemGroup,
} from '@/shared/timeline-scope-items';

const COLLAPSE_AFTER = 8;

const GROUP_ORDER: ScopedItemGroup[] = ['included', 'excluded', 'fileType'];

export type TimelineScopeSummaryProps = {
  scopes: ScopesObject;
  onChange: (next: ScopesObject) => void;
  /** Overrides the default basename / file-type key label. */
  resolveLabel?: (item: ScopedItem) => string;
  title?: string;
  emptyText?: string;
  onAddEvidence?: () => void;
  onEdit?: () => void;
  /** Restore the original cited / scoped set (chat wand). */
  onReset?: () => void;
  resetDisabled?: boolean;
  /** Hide when clearing every item would block submit (chat wand). */
  showClearAll?: boolean;
  /** Tags sit on the modal's dark surface. */
  inverse?: boolean;
  collapseAfter?: number;
};

function tagColor(item: ScopedItem): 'neutral' | 'danger' | 'brand' {
  if (item.state === 'excluded') return 'danger';
  if (item.kind === 'fileType') return 'brand';
  return 'neutral';
}

export function TimelineScopeSummary({
  scopes,
  onChange,
  resolveLabel,
  title,
  emptyText,
  onAddEvidence,
  onEdit,
  onReset,
  resetDisabled = false,
  showClearAll = true,
  inverse = false,
  collapseAfter = COLLAPSE_AFTER,
}: TimelineScopeSummaryProps) {
  const t = useTimePilotT();
  const [expanded, setExpanded] = useState(false);
  const items = listScopedItems(scopes);
  const resolvedTitle = title ?? t('timelineCreate.sourcesForTimeline');
  const resolvedEmpty = emptyText ?? t('timelineCreate.allCaseFiles');
  const groupLabel: Record<ScopedItemGroup, string> = {
    included: t('timelineCreate.included'),
    excluded: t('timelineCreate.excluded'),
    fileType: t('timelineCreate.fileTypes'),
  };
  const heading = (
    <p className="m-0 text-sm font-medium text-white">
      {items.length > 0
        ? t('timelineCreate.headingWithCount', { title: resolvedTitle, count: items.length })
        : resolvedTitle}
    </p>
  );

  if (items.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {heading}
        <p className="m-0 text-sm text-white/70">{resolvedEmpty}</p>
        {onReset || onAddEvidence ? (
          <div className="flex flex-row items-center gap-2">
            {onReset ? (
              <Button
                type="button"
                size="sm"
                style="text"
                color="light"
                background={inverse ? 'inverse' : 'default'}
                aria-label={t('timelineCreate.restoreSources')}
                disabled={resetDisabled}
                onClick={onReset}
              >
                {t('timelineCreate.reset')}
              </Button>
            ) : null}
            {onAddEvidence ? (
              <Button
                type="button"
                size="sm"
                style="solid"
                color="secondary"
                background={inverse ? 'inverse' : 'default'}
                onClick={onAddEvidence}
              >
                {t('timelineCreate.pickFiles')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  const visible = expanded ? items : items.slice(0, collapseAfter);
  const hiddenCount = items.length - visible.length;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row items-center justify-between gap-2">
        {heading}
        <div className="flex flex-row items-center gap-1">
          {onEdit ? (
            <Button
              type="button"
              size="sm"
              style="text"
              color="light"
              background={inverse ? 'inverse' : 'default'}
              onClick={onEdit}
            >
              {t('timelineCreate.edit')}
            </Button>
          ) : null}
          {onReset ? (
            <Button
              type="button"
              size="sm"
              style="text"
              color="light"
              background={inverse ? 'inverse' : 'default'}
              aria-label={t('timelineCreate.restoreSources')}
              disabled={resetDisabled}
              onClick={onReset}
            >
              {t('timelineCreate.reset')}
            </Button>
          ) : null}
          {showClearAll ? (
            <Button
              type="button"
              size="sm"
              style="text"
              color="light"
              background={inverse ? 'inverse' : 'default'}
              onClick={() => onChange(clearScopedItems(scopes))}
            >
              {t('timelineCreate.clearAll')}
            </Button>
          ) : null}
        </div>
      </div>
      {GROUP_ORDER.map((group) => {
        const groupItems = visible.filter((item) => item.group === group);
        if (groupItems.length === 0) return null;
        return (
          <div key={group} className="flex flex-col gap-1">
            <p className="m-0 text-xs text-white/55">{groupLabel[group]}</p>
            <div className="flex flex-wrap gap-2">
              {groupItems.map((item) => {
                const label = resolveLabel ? resolveLabel(item) : item.label;
                const tag = (
                  <Tag
                    label={label}
                    inverse={inverse}
                    color={tagColor(item)}
                    onRemove={() => onChange(removeScopedItem(scopes, item))}
                  />
                );
                if (item.kind === 'fileType' || label === item.path) {
                  return <React.Fragment key={item.id}>{tag}</React.Fragment>;
                }
                return (
                  <TpTooltip key={item.id} content={item.path} delayDuration={300}>
                    {tag}
                  </TpTooltip>
                );
              })}
            </div>
          </div>
        );
      })}
      {hiddenCount > 0 ? (
        <Button
          type="button"
          size="sm"
          style="text"
          color="light"
          background={inverse ? 'inverse' : 'default'}
          onClick={() => setExpanded(true)}
        >
          {t('timelineCreate.showAll', { count: items.length })}
        </Button>
      ) : null}
    </div>
  );
}
